/**
 * Service pour afficher les noms des votants au lieu des hashes
 */

import { supabase } from "@core/lib/supabase";
import {
  generateCreativeIdentity,
  getCachedCreativeIdentity,
} from "@core/lib/creativeIdentity";
import { logger } from "@core/lib/logger";

export interface VoterDisplayInfo {
  id: string;
  displayName: string;
  actualName?: string;
  isGuest: boolean;
}

class VotingDisplayNameService {
  private cache = new Map<string, VoterDisplayInfo>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Récupère le nom d'affichage pour un votant
   */
  public async getVoterDisplayInfo(
    respondentName: string,
  ): Promise<VoterDisplayInfo> {
    // ð³ Désactiver le cache temporairement pour forcer la recherche dans poll_responses
    // const cached = this.cache.get(respondentName);
    // if (cached) {
    //   logger.debug("Using cached display name", "vote", { displayName: cached.displayName, ageHours: (Date.now() - parseInt(cached.id)) / (1000 * 60 * 60) });
    //   return cached;
    // }

    logger.info(
      "Fetching display name for respondent (cache disabled)",
      "vote",
      { respondentName },
    );

    try {
      // ð³ PRIORITÉ ABSOLUE : Chercher d'abord dans les réponses de sondage (poll_responses) pour voterName
      const { data: pollData, error: pollError } = await supabase
        .from("poll_responses")
        .select("response_data")
        .eq("respondent_name", respondentName)
        .eq("response_type", "date")
        .order("created_at", { ascending: false })
        .limit(1);

      if (!pollError && pollData && pollData.length > 0) {
        const latestPoll = pollData[0];
        const voterName = latestPoll.response_data?.voterName;

        if (voterName && voterName !== "NO_NAME" && voterName.trim()) {
          logger.info("Found voter name in poll_responses", "vote", {
            respondentName,
            voterName,
            responseData: latestPoll.response_data,
          });

          const displayInfo: VoterDisplayInfo = {
            id: respondentName,
            displayName: voterName.trim(),
            actualName: voterName.trim(),
            isGuest: true,
          };

          this.cache.set(respondentName, {
            ...displayInfo,
            id: Date.now().toString(),
          });
          return displayInfo;
        }
      }

      // Chercher si c'est un utilisateur authentifié
      // NOTE: respondentName peut être un fingerprint (invité) ou un ID de profil (authentifié)
      let userData = null;
      let userError = null;

      try {
        const result = await supabase
          .from("profiles")
          .select("id, full_name, email") // Colonnes qui existent réellement
          .eq("id", respondentName)
          .single();

        userData = result.data;
        userError = result.error;
      } catch (err) {
        // Erreur de connexion ou autre problème
        userError = err;
      }

      if (!userError && userData) {
        const displayInfo: VoterDisplayInfo = {
          id: userData.id,
          displayName: userData.full_name || userData.email || "Utilisateur",
          actualName: userData.full_name,
          isGuest: false,
        };

        this.cache.set(respondentName, {
          ...displayInfo,
          id: Date.now().toString(),
        });
        return displayInfo;
      }

      // Chercher d'abord dans les réponses de sondage (poll_responses) pour voterName
      try {
        const { data: pollData } = await supabase
          .from("poll_responses")
          .select("response_data")
          .eq("respondent_name", respondentName)
          .eq("response_type", "date")
          .order("created_at", { ascending: false })
          .limit(1);

        if (pollData && pollData.length > 0) {
          const latestPoll = pollData[0];
          const voterName = latestPoll.response_data?.voterName;

          if (voterName && voterName !== "NO_NAME" && voterName.trim()) {
            logger.debug("Found voter name in poll_responses", "vote", {
              respondentName,
              voterName,
            });

            const displayInfo: VoterDisplayInfo = {
              id: respondentName,
              displayName: voterName.trim(),
              actualName: voterName.trim(),
              isGuest: true,
            };

            this.cache.set(respondentName, {
              ...displayInfo,
              id: Date.now().toString(),
            });
            return displayInfo;
          }
        }
      } catch (error) {
        logger.debug("Failed to fetch from poll_responses", "vote", { error });
      }

      // Chercher ensuite dans les réponses de formulaire (FormPolls)
      try {
        const { data: formData } = await supabase
          .from("form_responses")
          .select("respondent_name, respondent_email")
          .eq("respondent_name", respondentName)
          .limit(1);

        if (formData && formData.length > 0) {
          const form = formData[0];
          const displayName = form.respondent_email || form.respondent_name;

          const displayInfo: VoterDisplayInfo = {
            id: respondentName,
            displayName: displayName.trim(),
            actualName: form.respondent_name,
            isGuest: true,
          };

          this.cache.set(respondentName, {
            ...displayInfo,
            id: Date.now().toString(),
          });
          return displayInfo;
        }
      } catch (error) {
        logger.debug("Failed to fetch from form_responses", "vote", { error });
      }

      // Cas spécial : si respondent_name est un nom réel (ex: "MOI"), l'utiliser directement
      if (
        respondentName.length < 50 &&
        !respondentName.includes("f5c79") &&
        !respondentName.match(/^[a-f0-9]{32,}$/i)
      ) {
        logger.debug("Using real name as display name", "vote", {
          respondentName,
        });

        const displayInfo: VoterDisplayInfo = {
          id: respondentName,
          displayName: respondentName.trim(),
          actualName: respondentName.trim(),
          isGuest: true,
        };

        this.cache.set(respondentName, {
          ...displayInfo,
          id: Date.now().toString(),
        });
        return displayInfo;
      }

      // Si rien trouvé, générer une identité créative anonyme
      logger.debug("Generating creative identity for anonymous voter", "vote", {
        respondentName: respondentName.substring(0, 16) + "...",
      });

      const creativeIdentity = await getCachedCreativeIdentity(respondentName);

      const displayInfo: VoterDisplayInfo = {
        id: respondentName,
        displayName: creativeIdentity.displayName, // Ex: "Lion Doré #42" au lieu de "3825...9279"
        isGuest: true,
      };

      this.cache.set(respondentName, {
        ...displayInfo,
        id: Date.now().toString(),
      });

      logger.info("Creative identity generated for voter", "vote", {
        originalHash: respondentName.substring(0, 16) + "...",
        displayName: creativeIdentity.displayName,
        category: creativeIdentity.category,
      });

      return displayInfo;
    } catch (error) {
      logger.error("Error getting voter display info", "vote", { error });

      // Fallback: générer identité créative simple
      try {
        const creativeIdentity = generateCreativeIdentity(respondentName);
        const fallbackInfo: VoterDisplayInfo = {
          id: respondentName,
          displayName: creativeIdentity.displayName,
          isGuest: true,
        };

        logger.warn("Used fallback creative identity", "vote", {
          displayName: creativeIdentity.displayName,
        });

        return fallbackInfo;
      } catch (fallbackError) {
        logger.error("Creative identity fallback failed", "vote", {
          fallbackError,
        });

        // Ultra fallback: format de hash simple
        const ultraFallbackInfo: VoterDisplayInfo = {
          id: respondentName,
          displayName: this.formatHashDisplay(respondentName),
          isGuest: true,
        };

        return ultraFallbackInfo;
      }
    }
  }

  /**
   * Formate un hash pour l'afficher de manière lisible
   */
  private formatHashDisplay(hash: string): string {
    if (hash.length <= 8) return hash;

    // Afficher les 4 premiers et 4 derniers caractères
    return `${hash.substring(0, 4)}...${hash.substring(hash.length - 4)}`;
  }

  /**
   * Récupère les infos d'affichage pour plusieurs votants en batch
   */
  async getMultipleVoterDisplayInfos(
    respondentNames: string[],
  ): Promise<Map<string, VoterDisplayInfo>> {
    const results = new Map<string, VoterDisplayInfo>();

    // Traiter en parallèle avec un maximum de 5 requêtes simultanées
    const batchSize = 5;
    for (let i = 0; i < respondentNames.length; i += batchSize) {
      const batch = respondentNames.slice(i, i + batchSize);
      const batchPromises = batch.map(async (name) => {
        const info = await this.getVoterDisplayInfo(name);
        return { name, info };
      });

      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(({ name, info }) => {
        results.set(name, info);
      });
    }

    return results;
  }

  /**
   * Vide le cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Nettoie le cache des entrées expirées
   */
  cleanupCache(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - parseInt(value.id) > this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }
}

// Singleton
export const votingDisplayNameService = new VotingDisplayNameService();
